import React, { useMemo, useState, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, Dialog, DialogContent, Grid2 as Grid, IconButton, Typography } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { z } from 'zod';
import SelectField from '../../components/SelectField';
import TextField from '../../components/TextField';
import MultiSelectField from '../../components/MultiSelectField';
import Table from '../../components/Table';
import DownloadDetailsForm from './DownloadDetailsForm';
import CustomLink from '../../components/CustomLink';
import { BUSINESS_MODELS, COMMISSION_TYPES, DOWNLOAD_TABLE_COLUMNS, MARKETS_LIST } from '../../constants';
import CompanyLogo from '../../components/CompanyLogo';

const schema = z
  .object({
    company: z.string().min(1, 'Company is required'),
    productName: z.string().min(1, 'Product name is required'),
    category: z.string().min(1, 'Category is required'),
    otherCategory: z.string(),
    website: z.string().optional(),
    businessModel: z.string().min(1, 'Business Model is required'),
    otherBusinessModel: z.string(),
    summary: z.string(),
    uspsWhyUs: z.string(),
    idealCustomerProfile: z.string(),
    targetProfiles: z.string(),
    blocklist: z.string(),
    markets: z.array(z.string()).min(1, 'Please select at least one market'),
    averageMRR: z.string().refine((val) => val === 'N/A' || (!isNaN(val) && parseFloat(val) > 0), {
      message: "Please enter a valid positive number or 'N/A'.",
    }),

    commissionType: z.string().min(1, 'Commission Type is required'),
    commissionFirstTerm: z.string().refine((val) => val === 'N/A' || (!isNaN(val) && val >= 0 && val <= 100), {
      message: "Please enter a valid percentage between 0 and 100 or 'N/A'.",
    }),
    commissionRenewals: z.string().refine((val) => val === 'N/A' || (!isNaN(val) && val >= 0 && val <= 100), {
      message: "Please enter a valid percentage between 0 and 100 or 'N/A'.",
    }),
    duration: z.string().refine((val) => val === 'No Limit' || (!isNaN(val) && parseInt(val) > 0), {
      message: "Please enter a valid positive number or 'No Limit'.",
    }),

    // averageTotalCommissionValue: z.string(),
    averageConversionRate: z.string().refine((val) => !isNaN(val) && parseFloat(val) >= 0 && parseFloat(val) <= 100, {
      message: 'Please enter a valid percentage between 0 and 100.',
    }),
    averageSalesCycle: z.string().refine((val) => !isNaN(val) && parseInt(val) > 0, {
      message: 'Please enter a valid positive number.',
    }),

    churn: z.string().refine((val) => !isNaN(val) && parseFloat(val) >= 0 && parseFloat(val) <= 100, {
      message: 'Please enter a valid percentage between 0 and 100.',
    }),

    oneOffCommissions: z.string(),
    conditions: z.string(),
    buyOutOptions: z.string(),
  })
  .refine((data) => !(data.category === 'Other' && !data.otherCategory), {
    path: ['otherCategory'],
    message: 'This field is required',
  })
  .refine((data) => !(data.businessModel === 'Other' && !data.otherBusinessModel), {
    path: ['otherBusinessModel'],
    message: 'This field is required',
  });

const defaultFormValues = {
  company: '',
  productName: '',
  category: '',
  otherCategory: '',
  website: '',
  businessModel: '',
  otherBusinessModel: '',
  summary: '',
  uspsWhyUs: '',
  idealCustomerProfile: '',
  targetProfiles: '',
  blocklist: '',
  markets: [],
  averageMRR: '',
  commissionType: '',
  commissionFirstTerm: '',
  commissionRenewals: '',
  duration: '',
  // averageTotalCommissionValue: '',
  averageConversionRate: '',
  averageSalesCycle: '',
  churn: '',
  oneOffCommissions: '',
  conditions: '',
  buyOutOptions: '',
};

const numberFields = ['averageMRR', 'commissionFirstTerm', 'commissionRenewals'];

const getDefaultValue = ([key, value]) => {
  if (numberFields.includes(key) && !value) {
    return [key, 'N/A'];
  }
  if (key === 'duration' && !value) {
    return [key, 'No Limit'];
  }
  if (typeof value === 'number') {
    return [key, String(value)];
  }
  return [key, value];
};

const ProductDetailsForm = ({ isAdmin = false, companies = [], productData = {}, isLoading, onConfirm }) => {
  const [downloads, setDownloads] = useState(productData.downloads || []);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

  const methods = useForm({
    resolver: zodResolver(schema),
    mode: 'onBlur', // ✅ This line is added
    defaultValues: {
      ...Object.fromEntries(
        Object.entries({
          ...defaultFormValues,
          ...productData,
        }).map(getDefaultValue),
      ),
    },
  });

  const { handleSubmit } = methods;

  const businessModels = useMemo(
    () => BUSINESS_MODELS.map((item) => ({ label: item, value: item })),
    [], // ✅ Fix: Removed BUSINESS_MODELS
  );

  const commissionTypes = useMemo(
    () => COMMISSION_TYPES.map((item) => ({ label: item, value: item })),
    [], // ✅ Fix: Removed COMMISSION_TYPES
  );

  const marketsList = useMemo(
    () => MARKETS_LIST.map((item) => ({ label: item, value: item })),
    [], // ✅ Fix: Removed MARKETS_LIST
  );

  const companiesList = useMemo(
    () =>
      companies.map((item) => ({
        value: item._id,
        label: (
          <Box display='flex' alignItems='center' gap={1}>
            <CompanyLogo url={item.companyLogo} />
            <Typography>{item.companyName}</Typography>
          </Box>
        ),
      })),
    [companies],
  );

  const handleDeleteDownload = useCallback(
    (download) => {
      setDownloads((prevDownloads) => prevDownloads.filter((item) => item.url !== download.url));
    },
    [], // ✅ Empty dependency array to keep function reference stable
  );

  const downloadsTableData = useMemo(
    () =>
      downloads.map((item) => ({
        ...item,
        // logo: item.logo ? <img src={item.logo} alt={item.logo} height='50px' /> : '',
        url: item.url ? (
          <Box
            color='inherit'
            component={CustomLink}
            to={item.url}
            target='_blank'
            sx={{
              display: 'block',
              textDecoration: 'none',
              color: 'primary.main',
              width: 'max-content',
              maxWidth: '500px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {item.url}
          </Box>
        ) : undefined,
        actions: (
          <Box display='flex' justifyContent='end'>
            <IconButton color='error' onClick={() => handleDeleteDownload(item)}>
              <DeleteIcon />
            </IconButton>
          </Box>
        ),
      })),
    [downloads, handleDeleteDownload], // ✅ Fix: Added handleDeleteDownload
  );

  const onSubmit = (data) => {
    onConfirm({ ...data, downloads });
  };

  const handleAddDownload = (download) => {
    if (downloads.indexOf((item) => item.url === download.url) >= 0) {
      setDownloads(downloads.filter((item) => item.url !== download.url));
    } else {
      setDownloads([...downloads, download]);
    }
    setIsDownloadModalOpen(false);
  };

  return (
    <Box
      display='flex'
      flexDirection='column'
      alignItems='center'
      justifyContent='center'
      minHeight='100%'
      paddingX={2}
      paddingY={4}
    >
      <Typography variant='h4' gutterBottom>
        {!productData?.productName ? 'Create Program' : 'Edit Program'}
      </Typography>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <SelectField
                label='Company'
                name='company'
                options={companiesList}
                disabled={!!productData?.productName || !isAdmin}
              />
            </Grid>
            <Grid size={6}>
              <TextField label='Product Name' name='productName' placeholder='Enter your product name' />
            </Grid>

            <Grid size={6}>
              <SelectField
                label='Category'
                name='category'
                options={[
                  { label: 'AdTech', value: 'AdTech' },
                  { label: 'MarTech', value: 'MarTech' },
                  { label: 'FashionTech', value: 'FashionTech' },
                  { label: 'AITech', value: 'AITech' },
                  { label: 'Other', value: 'Other' },
                ]}
              />
            </Grid>

            {methods.watch('category') === 'Other' && (
              <Grid size={6}>
                <TextField
                  label='Other Category'
                  name='otherCategory'
                  placeholder='Enter the category name (e.g., AdTech, MarTech, etc.)'
                />
              </Grid>
            )}

            <Grid size={6}>
              <TextField label='Website URL' name='website' placeholder='Enter your website (e.g., yourssite.com)' />
            </Grid>
            <Grid size={6}>
              <SelectField label='Business Model' name='businessModel' options={businessModels} />
            </Grid>

            {methods.watch('businessModel') === 'Other' && (
              <Grid size={6}>
                <TextField
                  label='Other Business Model'
                  name='otherBusinessModel'
                  placeholder='Explain your business model in a few words'
                />
              </Grid>
            )}

            <TextField label='Summary' name='summary' multiline placeholder='Describe your product in a few lines' />
            <Grid size={6}>
              <TextField
                label='Value Proposition'
                name='uspsWhyUs'
                multiline
                placeholder='Explain the features and benefits of your product'
              />
            </Grid>
            <Grid size={6}>
              <TextField
                label='Ideal Customer Profile'
                name='idealCustomerProfile'
                multiline
                placeholder='Explain the target customer profile for your product'
              />
            </Grid>
            <Grid size={6}>
              <TextField
                label='Key Decision Makers'
                name='targetProfiles'
                multiline
                placeholder='List decision maker roles (e.g., CEO, CMO). Use commas.'
              />
            </Grid>
            <Grid size={6}>
              <TextField
                label='Blocklist'
                name='blocklist'
                multiline
                placeholder='List roles that cannot be contacted'
              />
            </Grid>
            <Grid size={6}>
              <MultiSelectField label='Markets' name='markets' options={marketsList} />
            </Grid>
            <Grid size={6}>
              <TextField
                label='Average MRR ($)'
                name='averageMRR'
                placeholder="Enter the average MRR (e.g., 4000) or 'N/A' if not applicable."
                type='text'
                inputProps={{
                  pattern: '^[0-9]*$|^N/A$',
                }}
              />
            </Grid>

            <Grid size={6}>
              <SelectField label='Commission Type' name='commissionType' options={commissionTypes} />
            </Grid>
            <Grid size={6}>
              <TextField
                label='Commission First Term (%)'
                name='commissionFirstTerm'
                placeholder="Enter the commission percentage (0-100) or 'N/A' if not applicable."
                type='text'
                inputProps={{
                  pattern: '^[0-9]*$|^N/A$',
                  max: 100,
                  min: 0,
                }}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                label='Commission Renewals (%)'
                name='commissionRenewals'
                placeholder="Enter the commission percentage (0-100) or 'N/A' if not applicable."
                type='text'
                inputProps={{
                  pattern: '^[0-9]*$|^N/A$',
                  max: 100,
                  min: 0,
                }}
              />
            </Grid>

            <Grid size={6}>
              <TextField
                label='Duration (Months)'
                name='duration'
                placeholder="Enter the duration in months (e.g., 12) or 'No Limit' if perpetual."
                type='text'
                inputProps={{
                  pattern: '^[0-9]*$|^No Limit$',
                }}
              />
            </Grid>

            {/*<Grid size={6}>*/}
            {/*  <TextField*/}
            {/*    label='Average Total Commission Value ($)'*/}
            {/*    name='averageTotalCommissionValue'*/}
            {/*    placeholder='Enter amount or N/A'*/}
            {/*  />*/}
            {/*</Grid>*/}
            <Grid size={6}>
              <TextField
                label='Average Conversion Rate (%)'
                name='averageConversionRate'
                placeholder='Enter the conversion rate (0-100) of meetings to deals.'
                type='text'
                inputProps={{
                  pattern: '^[0-9]*$',
                  max: 100,
                  min: 0,
                }}
              />
            </Grid>

            <Grid size={6}>
              <TextField
                label='Average Sales Cycle (weeks)'
                name='averageSalesCycle'
                placeholder='Enter the number of weeks from contact to deal.'
                type='text'
                inputProps={{
                  pattern: '^[0-9]*$',
                  min: 1,
                }}
              />
            </Grid>

            <Grid size={6}>
              <TextField
                label='Churn (%)'
                name='churn'
                placeholder='Enter churn rate (0-100) — clients lost within 2 years.'
                type='text'
                inputProps={{
                  pattern: '^[0-9]*$',
                  min: 0,
                  max: 100,
                }}
              />
            </Grid>

            <Grid size={6}>
              <TextField
                label='Event-Based Commission'
                name='oneOffCommissions'
                multiline
                placeholder='Enter Event-Based Commission.'
              />
            </Grid>

            <Grid size={6}>
              <TextField
                label='Conditions'
                name='conditions'
                multiline
                placeholder='List additional conditions for commission eligibility.'
              />
            </Grid>
            <Grid size={6}>
              <TextField
                label='Buy Out Options'
                name='buyOutOptions'
                multiline
                placeholder='Describe buy-out terms for future commission eligibility.'
              />
            </Grid>
          </Grid>

          <Box display='flex' justifyContent='space-between' alignItems='center' mt={3} mb={2}>
            <Typography>Downloads</Typography>
            <Button
              variant='contained'
              color='primary'
              disabled={downloads.length >= 10}
              onClick={() => setIsDownloadModalOpen(true)}
            >
              Add download item
            </Button>
          </Box>
          <Table columns={DOWNLOAD_TABLE_COLUMNS} data={downloadsTableData} rowsPerPage={3} />

          <Box mt={3} mb={2}>
            <Button type='submit' variant='contained' color='primary' fullWidth disabled={isLoading}>
              Submit
            </Button>
          </Box>
        </form>
      </FormProvider>

      <Dialog open={isDownloadModalOpen} onClose={() => setIsDownloadModalOpen(false)}>
        <DialogContent>
          <DownloadDetailsForm onConfirm={handleAddDownload} />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ProductDetailsForm;
